import React, { createContext, useContext, useState, useEffect } from "react";
import { parseDiscontinued } from "./utils";
import { isTstPlaceholder } from "./utils/imageHelpers";
import api, { fetchRoots, getCurrentAnnouncement } from "./api";

const API_BASE = "http://40.40.69.79:3051";

const DataContext = createContext();

export function DataProvider({ children }) {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rootCategories, setRootCategories] = useState([]);
  const [categoryHierarchy, setCategoryHierarchy] = useState({});
  const [navRoots, setNavRoots] = useState([]);
  const [announcements, setAnnouncements] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState("ZAR");
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const fetchAllData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch products (required)
        const productsRes = await api.get("/api/v1/seritonex/products/all", {
          responseType: "json",
        });

        // Get currency from products response headers
        const apiCurrency = productsRes.headers?.["x-currency"];
        if (apiCurrency) setCurrency(apiCurrency);

        const productsData = productsRes.data;

        // Fetch categories separately so a failure doesn't block products
        let categoriesData = null;
        try {
          const categoriesRes = await api.get("/api/v1/seritonex/category/all");
          categoriesData = categoriesRes.data;
        } catch (catErr) {
          console.warn("Categories fetch failed (non-fatal):", catErr);
        }

        // Fetch nav roots (non-fatal)
        try {
          const roots = await fetchRoots();
          const rootsArr = Array.isArray(roots) ? roots : Array.isArray(roots?.value) ? roots.value : [];
          if (!cancelled) setNavRoots(rootsArr);
        } catch (rootsErr) {
          console.warn("Nav roots fetch failed (non-fatal):", rootsErr);
        }

        // Fetch current announcement (non-fatal)
        try {
          const announcementData = await getCurrentAnnouncement();
          if (!cancelled) setAnnouncements(announcementData);
        } catch (annErr) {
          console.warn("Announcements fetch failed (non-fatal):", annErr);
        }

        if (!Array.isArray(productsData)) {
          throw new Error("Invalid products data format");
        }

        const normalizedProducts = productsData.map((d) => {
          const variants = Array.isArray(d.variants) ? d.variants : [];
          const totalStock = variants.reduce(
            (sum, v) => sum + (v.quantity_on_hand || 0),
            0,
          );

          let displayPrice = d.value_incl || d.value_excl || d.price || 0;
          if (variants.length > 0) {
            displayPrice =
              variants[0].value_incl ||
              variants[0].value_excl ||
              variants[0].price ||
              displayPrice;
          }

          let primaryImageUrl = "";

          if (
            d.primary_image?.path &&
            !isTstPlaceholder(d.primary_image.path)
          ) {
            primaryImageUrl = d.primary_image.path;
          } else if (
            variants.length > 0 &&
            variants[0].images?.length > 0 &&
            !isTstPlaceholder(variants[0].images[0].path)
          ) {
            primaryImageUrl = variants[0].images[0].path;
          } else if (
            variants.length > 0 &&
            variants[0].primary_image &&
            !isTstPlaceholder(variants[0].primary_image.path)
          ) {
            primaryImageUrl = variants[0].primary_image.path;
          }

          let secondaryImageUrl = null;
          if (
            variants.length > 0 &&
            variants[0].images?.length > 0 &&
            !isTstPlaceholder(variants[0].images[0].path)
          ) {
            secondaryImageUrl = variants[0].images[0].path;
          }

          return {
            id: d.product_id,
            title: d.title,
            name: d.title,
            price: displayPrice,
            valueExcl: d.value_excl || 0,
            valueIncl: d.value_incl || 0,
            description: d.description || "",
            brand: d.brand || "",
            image: primaryImageUrl,
            secondary_image: secondaryImageUrl,
            sku: d.sku || "",
            weight: parseFloat(d.weight) || 0,
            qty: totalStock,
            quantity: totalStock,
            quantity_on_hand: totalStock,
            category: d.category?.description || "",
            subCategory: d.sub_category?.description || "",
            categories: {
              main: d.category?.description || "",
              sub: d.sub_category?.description || "",
            },
            categoryId: d.category?.category_id,
            subCategoryId: d.sub_category?.category_id,
            category_id: d.category?.category_id,
            sub_category_id: d.sub_category?.category_id,
            main_category_id: d.category?.category_id,
            category_relationship_id: d.category?.category_relationship_id,
            classification_id: d.category?.classification_id,
            discontinued: parseDiscontinued(d.discontinued),
            primary_image: d.primary_image || null,
            images: Array.isArray(d.images) ? d.images : [],
            primaryImageUrl: primaryImageUrl,
            variations: variants,
          };
        });

        if (cancelled) return;

        setProducts(normalizedProducts);

        // Process categories to build hierarchy
        if (Array.isArray(categoriesData)) {
          // Extract unique category descriptions
          const categoryList = [];
          const categoryMap = new Map();

          categoriesData.forEach((cat) => {
            if (cat && cat.description && typeof cat.description === "string") {
              const desc = cat.description.trim();
              if (desc && !categoryList.includes(desc)) {
                categoryList.push(desc);
              }
              categoryMap.set(cat.category_id, cat);
            }
          });

          setCategories(categoryList.sort());

          // Build hierarchy (roots and children)
          const roots = categoriesData.filter(
            (cat) => !cat.parent_id || cat.parent_id === cat.category_id,
          );
          const hierarchyMap = {};

          roots.forEach((root) => {
            hierarchyMap[root.category_id] = categoriesData.filter(
              (cat) =>
                cat.parent_id === root.category_id &&
                cat.category_id !== root.category_id,
            );
          });

          setRootCategories(roots);
          setCategoryHierarchy(hierarchyMap);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to fetch data:", err);
        setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchAllData(); 
    return () => {
      cancelled = true;
    };
  }, []);

  const value = {
    products,
    categories,
    rootCategories,
    categoryHierarchy,
    navRoots,
    announcements,
    loading,
    currency,
    error,
    setProducts, // Allow updates for inventory changes
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
}

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error("useData must be used within a DataProvider");
  }
  return context;
};